import { APP_BASE_HREF } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import {
  type ActivatedRouteSnapshot,
  type GuardResult,
  type Params,
  RedirectCommand,
  Router,
  type RouterStateSnapshot,
} from '@angular/router';
import { isServerDownError } from '@reconcept-platform/shared/util';
import { lastValueFrom, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { WebSessionService } from './web-session.service';

@Injectable({
  providedIn: 'root',
})
export class WebSessionGuard {
  private session = inject(WebSessionService);
  private appBaseHref = inject(APP_BASE_HREF);
  private router = inject(Router);

  // canMatch runs on every navigation; after one verified session, an expired token gets a 401 and is refreshed
  private sessionVerified = false;

  // runs during route matching, so before the canMatch guards of the child routes, which may query the API
  async canMatch(): Promise<GuardResult> {
    // canMatch gets no router state, the navigation has the full target url
    const targetUrl = this.router.currentNavigation()?.extractedUrl;
    if (!targetUrl) {
      throw new Error('WebSessionGuard can only run as canMatch during a navigation');
    }

    return this.checkSession(this.router.serializeUrl(targetUrl), targetUrl.queryParams);
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<GuardResult> {
    return this.checkSession(state.url, route.queryParams);
  }

  private async checkSession(url: string, queryParams: Params): Promise<GuardResult> {
    if (this.sessionVerified && (await lastValueFrom(this.session.isLoggedIn()))) {
      return true;
    }

    // if users then go to the main url, we do not want them to login
    // so we logout if they have an invite token.
    const hasInviteSession = await this.session.hasInviteToken();
    if (hasInviteSession) {
      await this.session.logout();
      return false;
    }

    // used for the PR link, where we need to redirect to a subfolder from login
    const baseHref = this.appBaseHref.slice(0, -1); // slice of the trailing /

    // temporary extraQueryParams, need to add redirect param to show notification to user if automatic redirect from v2.
    // when valid login, we need to remove ?redirect= from state.url, so encoding won't fail
    const result = await lastValueFrom(
      this.session
        .tryAccessTokenOrRedirect(baseHref + url, queryParams)
        .pipe(
          catchError((error) =>
            isServerDownError(error)
              ? of(new RedirectCommand(this.router.parseUrl('/error'), { skipLocationChange: true }))
              : throwError(() => error),
          ),
        ),
    );

    this.sessionVerified = result === true;
    return result;
  }
}
